import React, { useMemo, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Package, User, BarChart3, ShoppingCart, Trash2, Package2 } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useOrder } from '../../context/OrderContext';

const AdminDashboard: React.FC = () => {
  const { setCurrentScreen } = useApp();
  // const { } = useAuth(); // Removed unused destructuring
  const { products, orders, categories, customers, refreshAllData } = useData();
  const { deleteOrder } = useOrder();
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    orderId: string | null;
    orderInfo: string;
  }>({
    show: false,
    orderId: null,
    orderInfo: ''
  });
  
  // Debug: Log customers when they change
  useEffect(() => {
    console.log('AdminDashboard: Customers updated:', customers);
  }, [customers]);

  // Listen for customer registration events to refresh data
  useEffect(() => {
    const handleCustomerRegistered = async () => {
      console.log('🔄 AdminDashboard: Customer registered, refreshing data...');
      // Refresh all data when a new customer registers
      await refreshAllData();
    };

    window.addEventListener('customerRegistered', handleCustomerRegistered);
    return () => {
      window.removeEventListener('customerRegistered', handleCustomerRegistered);
    };
  }, [refreshAllData]);

  // Calculate real-time metrics from active data
  const dashboardMetrics = useMemo(() => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => {
      console.log('Order amount:', order.amount, 'Order ID:', order.id);
      return sum + (order.amount || 0);
    }, 0);
    const totalProducts = products.length;
    const totalCategories = categories.length;
    const totalCustomers = customers.length;
    
    console.log('AdminDashboard: Calculating metrics - customers:', customers.length, 'orders:', orders.length);
    console.log('AdminDashboard: Orders data:', orders);
    console.log('AdminDashboard: Total revenue calculated:', totalRevenue);
    
    // Calculate growth (mock for now, in real app would compare with previous period)
    const orderGrowth = totalOrders > 0 ? Math.floor(Math.random() * 20) + 5 : 0;
    const revenueGrowth = totalRevenue > 0 ? Math.floor(Math.random() * 15) + 2 : 0;
    const customerGrowth = totalCustomers > 0 ? Math.floor(Math.random() * 25) + 10 : 0;
    
    return {
      totalOrders,
      totalRevenue,
      totalProducts,
      totalCategories,
      totalCustomers,
      orderGrowth,
      revenueGrowth,
      customerGrowth
    };
  }, [orders, products, categories, customers]);

  // Get recent orders (last 5)
  const recentOrders = useMemo(() => {
    return orders
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [orders]);


  // Calculate order distribution by status
  const orderDistribution = useMemo(() => {
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = orders.length;
    return Object.entries(statusCounts).map(([status, count]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1),
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    }));
  }, [orders]);

  // Calculate revenue trend (last 7 days)
  const revenueTrend = useMemo(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last7Days.map(date => {
      const dayOrders = orders.filter(order => order.date === date);
      const amount = dayOrders.reduce((sum, order) => sum + order.amount, 0);
      return { date, amount };
    });
  }, [orders]);

  const handleProfile = () => {
    setCurrentScreen('admin-settings');
  };


  const handleOrdersClick = () => {
    setCurrentScreen('admin-orders');
  };

  const handleCustomersClick = () => {
    setCurrentScreen('admin-customers');
  };

  const handleProductsClick = () => {
    setCurrentScreen('admin-product-list');
  };

  const handleCategoriesClick = () => {
    setCurrentScreen('admin-category-list');
  };

  const handleCustomerBillingClick = () => {
    setCurrentScreen('admin-customer-billing');
  };

  const handleCustomerBillingHistoryClick = () => {
    setCurrentScreen('customer-billing-history');
  };

  const handleDeleteClick = (orderId: string, customerName: string, orderAmount: number) => {
    setDeleteConfirm({
      show: true,
      orderId,
      orderInfo: `${customerName} - ₹${orderAmount}`
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.orderId) {
      await deleteOrder(deleteConfirm.orderId);
      setDeleteConfirm({
        show: false,
        orderId: null,
        orderInfo: ''
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({
      show: false,
      orderId: null,
      orderInfo: ''
    });
  };

  const MetricCard: React.FC<{ 
    title: string; 
    value: string; 
    change: string; 
    icon: React.ReactNode; 
    positive?: boolean;
    onClick?: () => void;
  }> = ({ 
    title, value, change, icon, positive = true, onClick 
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: onClick ? 1.05 : 1 }}
      whileTap={{ scale: onClick ? 0.95 : 1 }}
      onClick={onClick}
      className={`food-card ${onClick ? 'cursor-pointer hover:shadow-xl' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="p-3 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl shadow-lg">
          {icon}
        </div>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${positive ? 'bg-green-500 bg-opacity-20 text-green-400' : 'bg-red-500 bg-opacity-20 text-red-400'}`}>
          {change}
        </span>
      </div>
      <h3 className="text-3xl font-bold text-white mb-1">{value}</h3>
      <p className="text-gray-400 text-sm">{title}</p>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center">
                <BarChart3 size={20} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
            </div>
            <motion.button
              onClick={handleProfile}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Profile"
            >
              <User size={24} />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Metrics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Orders"
            value={dashboardMetrics.totalOrders.toLocaleString()}
            change={`+${dashboardMetrics.orderGrowth}%`}
            icon={<Package2 size={20} className="text-teal-400" />}
            onClick={handleOrdersClick}
          />
          <MetricCard
            title="Categories"
            value={dashboardMetrics.totalCategories.toLocaleString()}
            change="Available"
            icon={<BarChart3 size={20} className="text-green-400" />}
            onClick={handleCategoriesClick}
          />
          <MetricCard
            title="Customers"
            value={dashboardMetrics.totalCustomers.toLocaleString()}
            change={`+${dashboardMetrics.customerGrowth}%`}
            icon={<Users size={20} className="text-purple-400" />}
            onClick={handleCustomersClick}
          />
          <MetricCard
            title="Products"
            value={dashboardMetrics.totalProducts.toLocaleString()}
            change="Active"
            icon={<ShoppingCart size={20} className="text-blue-400" />}
            onClick={handleProductsClick}
          />
        </div>

        {/* Quick Actions - Customer Billing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            onClick={handleCustomerBillingClick}
            className="food-card cursor-pointer hover:shadow-2xl transition-all duration-200 bg-gradient-to-r from-purple-900 to-teal-900 border-2 border-purple-500"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Customer Billing</h3>
                <p className="text-gray-300 text-sm">View and manage individual customer bills</p>
              </div>
              <div className="text-5xl">💳</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={handleCustomerBillingHistoryClick}
            className="food-card cursor-pointer hover:shadow-2xl transition-all duration-200 bg-gradient-to-r from-blue-900 to-green-900 border-2 border-blue-500"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Customer History</h3>
                <p className="text-gray-300 text-sm">Customer-wise billing history & analytics</p>
              </div>
              <div className="text-5xl">📊</div>
            </div>
          </motion.div>
        </div>

        {/* Revenue Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="food-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Revenue Trend</h3>
            <span className="text-sm px-3 py-1 bg-slate-700 text-gray-300 rounded-full">Last 7 Days</span>
          </div>
          <div className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent mb-2">
            ₹{dashboardMetrics.totalRevenue.toLocaleString()}
          </div>
          <div className="text-sm text-green-400 mb-4">+{dashboardMetrics.revenueGrowth}% from last week</div>
          <div className="h-24 flex items-end space-x-2">
            {revenueTrend.map((point, index) => {
              const maxAmount = Math.max(...revenueTrend.map(p => p.amount));
              const height = maxAmount > 0 ? (point.amount / maxAmount) * 100 : 0;
              return (
                <div
                  key={index}
                  className="flex-1 bg-gradient-to-t from-teal-500 via-teal-400 to-blue-400 rounded-t min-h-[4px] hover:opacity-80 transition-opacity cursor-pointer"
                  style={{ height: `${Math.max(height, 4)}%` }}
                  title={`${point.date}: ₹${point.amount}`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-3">
            {revenueTrend.map((point, index) => (
              <span key={index} className="text-center">
                {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Order Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="food-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Order Status</h3>
            <span className="text-sm px-3 py-1 bg-slate-700 text-gray-300 rounded-full">All Time</span>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{dashboardMetrics.totalOrders.toLocaleString()}</div>
          <div className="text-sm text-green-400 mb-4">+{dashboardMetrics.orderGrowth}% growth</div>
          <div className="space-y-3">
            {orderDistribution.length > 0 ? (
              orderDistribution.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-white font-medium text-sm">{item.status}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 h-2.5 bg-slate-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${item.percentage}%` }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className={`h-full rounded-full ${
                          item.status === 'Delivered' ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                          item.status === 'Shipped' ? 'bg-gradient-to-r from-blue-500 to-cyan-400' :
                          item.status === 'Pending' ? 'bg-gradient-to-r from-yellow-500 to-orange-400' :
                          'bg-gradient-to-r from-red-500 to-pink-400'
                        }`}
                      />
                    </div>
                    <span className="text-gray-400 text-sm font-semibold w-10 text-right">{item.percentage}%</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-400 text-sm">No orders yet</p>
              </div>
            )}
          </div>
        </motion.div>


        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="food-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Recent Orders</h3>
            <motion.button
              onClick={() => setCurrentScreen('admin-orders')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-teal-400 hover:text-teal-300 text-sm font-semibold"
            >
              View All →
            </motion.button>
          </div>
          <div className="space-y-3">
            {recentOrders.length > 0 ? (
              recentOrders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all duration-200 border border-gray-700"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                      order.status === 'delivered' ? 'bg-gradient-to-br from-green-500 to-emerald-400' :
                      order.status === 'shipped' ? 'bg-gradient-to-br from-blue-500 to-cyan-400' :
                      order.status === 'pending' ? 'bg-gradient-to-br from-yellow-500 to-orange-400' :
                      'bg-gradient-to-br from-red-500 to-pink-400'
                    }`}>
                      <Package size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-base">{order.customerName}</p>
                      <p className="text-gray-400 text-sm">Order #{order.id || 'N/A'} • {order.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-3">
                      <div>
                        <span className="text-white font-bold text-lg">₹{order.amount}</span>
                        <p className={`text-xs font-medium ${
                          order.status === 'delivered' ? 'text-green-400' :
                          order.status === 'shipped' ? 'text-blue-400' :
                          order.status === 'pending' ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </p>
                      </div>
                      <motion.button
                        onClick={() => handleDeleteClick(order.id, order.customerName, order.amount)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-slate-600 rounded-lg transition-colors duration-200"
                        title="Delete order"
                      >
                        <Trash2 size={16} />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Package size={32} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No Orders Yet</h3>
                <p className="text-gray-400 text-sm">
                  Orders will appear here once customers start placing them
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <BottomNavigation />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
          onClick={handleDeleteCancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="food-card max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Trash2 size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Delete Order</h3>
                <p className="text-gray-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-300 mb-3">
                Are you sure you want to delete this order?
              </p>
              <div className="bg-slate-700 rounded-xl p-4 border border-gray-600">
                <p className="text-white font-semibold">{deleteConfirm.orderInfo}</p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <motion.button
                onClick={handleDeleteCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleDeleteConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-lg"
              >
                Delete Order
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-700"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <Trash2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Order</h3>
                <p className="text-slate-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-300 mb-2">
                Are you sure you want to delete this order?
              </p>
              <div className="bg-slate-700 rounded-lg p-3">
                <p className="text-white font-medium">{deleteConfirm.orderInfo}</p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <motion.button
                onClick={handleDeleteCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleDeleteConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Delete Order
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
